import { useCallback, useEffect, useState } from "react";
import { LogOut, Plus, Clock, Users, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, formatBRT } from "@/lib/api";
import { LOGO_URL } from "@/data/servers";
import { NetworkStatus } from "@/components/staff/NetworkStatus";
import { GenerateDialog } from "@/components/staff/GenerateDialog";
import { PostList } from "@/components/staff/PostList";
import { StaffManager } from "@/components/staff/StaffManager";
import { ChangePasswordDialog } from "@/components/staff/ChangePasswordDialog";

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const [settings, setSettings] = useState(null);
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([api.get("/settings"), api.get("/posts")]);
    setSettings(s.data);
    setPosts(p.data);
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    const t = setInterval(() => load().catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [load]);

  const toggleAutopilot = async (v) => {
    try {
      const { data } = await api.put("/settings", { autopilot: v });
      setSettings(data);
      toast.success(v ? "Piloto automático ligado" : "Piloto automático desligado");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="dz-root min-h-screen" data-testid="staff-dashboard">
      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BRASIL+" className="w-10 h-10" />
            <span className="dz-heading text-xl font-extrabold uppercase tracking-wider text-white">
              Postador <span className="text-[#e02424]">IA</span>
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline dz-mono text-xs text-zinc-500" data-testid="staff-user-email">{user?.email}</span>
            {user?.role === "admin" && (
              <button onClick={() => setTeamOpen(true)} data-testid="open-staff-manager-button" className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white">
                <Users size={16} /> <span className="hidden sm:inline">Equipe</span>
              </button>
            )}
            <button onClick={() => setPwdOpen(true)} data-testid="open-change-password-button" className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white">
              <KeyRound size={16} /> <span className="hidden sm:inline">Senha</span>
            </button>
            <button onClick={logout} data-testid="logout-button" className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white">
              <LogOut size={16} /> Sair
            </button>
          </div>
        </div>
      </header>
      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <section className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="dz-mono text-xs uppercase tracking-[0.3em] text-[#f87171] mb-3">// Área da staff</p>
            <h1 className="dz-heading text-4xl sm:text-5xl font-black uppercase text-white leading-none">Central de posts</h1>
            <p className="mt-3 text-sm text-zinc-400 max-w-xl">
              A IA cria o texto e a imagem. Nada é publicado sem a sua aprovação. Horários diários: 18h, 20h e 22h (Brasília).
            </p>
          </div>
          <button onClick={() => setOpen(true)} data-testid="open-generate-dialog-button"
            className="dz-play self-start lg:self-auto flex items-center gap-2 px-6 py-3 bg-[#c81e1e] text-white dz-heading text-lg font-extrabold uppercase tracking-wider hover:brightness-110">
            <Plus size={18} /> Gerar post
          </button>
        </section>
        {settings && (
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
            <div className="border border-white/10 bg-[#121314] p-5" data-testid="autopilot-panel">
              <div className="flex items-center justify-between">
                <p className="dz-heading text-lg font-bold uppercase text-white">Piloto automático</p>
                <Switch checked={settings.autopilot} onCheckedChange={toggleAutopilot} data-testid="autopilot-switch"
                  className="data-[state=checked]:bg-[#c81e1e] data-[state=unchecked]:bg-zinc-700" />
              </div>
              <p className="text-xs text-zinc-400 mt-2">Quando ligado, a IA cria rascunhos para os próximos horários. Eles só são postados depois de aprovados.</p>
              <div className="mt-4 space-y-1">
                {settings.next_slots.map((s) => (
                  <p key={s} className="dz-mono text-xs text-zinc-300 flex items-center gap-2"><Clock size={12} className="text-[#f87171]" /> {formatBRT(s)}</p>
                ))}
              </div>
            </div>
            <NetworkStatus networks={settings.networks} isAdmin={user?.role === "admin"} onChanged={load} />
          </section>
        )}
        <PostList posts={posts} reload={load} />
      </main>
      <GenerateDialog open={open} onOpenChange={setOpen} onCreated={load} />
      {user?.role === "admin" && <StaffManager open={teamOpen} onOpenChange={setTeamOpen} />}
      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
    </div>
  );
}
